import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  DialogContentText,
  Pagination,
  CircularProgress,
  TextField,
  Grid,
  TableSortLabel,
  Collapse,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import ptBR from 'date-fns/locale/pt-BR';
import api from '../../services/api';
import Layout from '../Layout';

const HistoricoResponsaveisPanel = () => {
  const [historico, setHistorico] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [editingHistorico, setEditingHistorico] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [paginacao, setPaginacao] = useState({
    pagina_atual: 1,
    total_paginas: 1,
    total_registros: 0,
    registros_por_pagina: 50
  });
  const [filtros, setFiltros] = useState({
    empresa: '',
    responsavel: '',
    data_inicio_de: null,
    data_inicio_ate: null,
    data_fim_de: null,
    data_fim_ate: null
  });
  const [ordenacao, setOrdenacao] = useState({
    order_by: 'ID',
    order_direction: 'asc'
  });
  const [formData, setFormData] = useState({
    IDEmpresa: '',
    IDResponsavel: '',
    DataInicio: new Date(),
    DataFim: null,
    empresaSelecionada: null,
    responsavelSelecionado: null,
  });

  const fetchResponsaveis = useCallback(async () => {
    try {
      const response = await api.get('/responsaveis');
      setResponsaveis(response.data);
    } catch (error) {
      console.error('Erro ao buscar responsáveis:', error);
      setError('Erro ao carregar responsáveis. Por favor, tente novamente.');
    }
  }, []); // Sem dependências pois não usa estado local

  const fetchEmpresas = useCallback(async () => {
    try {
      const response = await api.get('/empresas');
      const empresasData = response.data?.empresas || response.data || [];
      const normalizadas = Array.isArray(empresasData)
        ? empresasData.map((e) => ({
            ID: e.ID ?? e.id,
            NomeFantasia: e.NomeFantasia ?? e['Nome Fantasia'] ?? e.nomeFantasia ?? e.razao_social ?? e.nome ?? '',
          }))
        : [];
      setEmpresas(normalizadas);
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
    }
  }, []);

  const fetchHistorico = useCallback(async (page = 1, customOrdenacao = null) => {
    try {
      setLoading(true);
      
      // Usar ordenação customizada ou a atual
      const ordenacaoAtual = customOrdenacao || ordenacao;
      
      // Construir query string com filtros e ordenação
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        order_by: ordenacaoAtual.order_by,
        order_direction: ordenacaoAtual.order_direction
      });
      
      // Adicionar filtros se preenchidos
      if (filtros.empresa) params.append('empresa', filtros.empresa);
      if (filtros.responsavel) params.append('responsavel', filtros.responsavel);
      if (filtros.data_inicio_de && filtros.data_inicio_de instanceof Date) {
        params.append('data_inicio_de', filtros.data_inicio_de.toISOString().split('T')[0]);
      }
      if (filtros.data_inicio_ate && filtros.data_inicio_ate instanceof Date) {
        params.append('data_inicio_ate', filtros.data_inicio_ate.toISOString().split('T')[0]);
      }
      if (filtros.data_fim_de && filtros.data_fim_de instanceof Date) {
        params.append('data_fim_de', filtros.data_fim_de.toISOString().split('T')[0]);
      }
      if (filtros.data_fim_ate && filtros.data_fim_ate instanceof Date) {
        params.append('data_fim_ate', filtros.data_fim_ate.toISOString().split('T')[0]);
      }
      
      const response = await api.get(`/historico-responsaveis?${params}`);
      
      // Verificar se a resposta tem a estrutura esperada
      if (response.data.dados && response.data.paginacao) {
        setHistorico(response.data.dados);
        setPaginacao(response.data.paginacao);
      } else {
        // Fallback para formato antigo (sem paginação)
        setHistorico(response.data);
        setPaginacao({
          pagina_atual: 1,
          total_paginas: 1,
          total_registros: response.data.length,
          registros_por_pagina: response.data.length
        });
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      setError('Erro ao carregar histórico. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filtros, ordenacao]); // Dependências do useCallback

  useEffect(() => {
    fetchHistorico(1); // Carregar primeira página
    fetchResponsaveis();
    fetchEmpresas();
  }, [fetchHistorico, fetchResponsaveis, fetchEmpresas]);



  const handlePageChange = (event, value) => {
    fetchHistorico(value);
  };

  const handleSort = (campo) => {
    const novaOrdenacao = {
      order_by: campo,
      order_direction: ordenacao.order_by === campo && ordenacao.order_direction === 'asc' ? 'desc' : 'asc'
    };
    setOrdenacao(novaOrdenacao);
    // Fazer nova consulta com a ordenação aplicada e voltar para página 1
    fetchHistorico(1, novaOrdenacao);
  };

  const handleFilterChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const aplicarFiltros = () => {
    fetchHistorico(1); // Voltar para primeira página ao aplicar filtros
  };

  const limparFiltros = () => {
    setFiltros({
      empresa: '',
      responsavel: '',
      data_inicio_de: null,
      data_inicio_ate: null,
      data_fim_de: null,
      data_fim_ate: null
    });
    // Após limpar, buscar novamente
    setTimeout(() => {
      fetchHistorico(1);
    }, 100);
  };

  const handleSubmit = async () => {
    try {
      setDialogError(''); // Limpar erro anterior
      
      // Verificar se já existe um responsável ativo para a empresa
      if (!editingHistorico && !formData.DataFim) {
        const responsavelAtivo = historico.find(
          h => h.IDEmpresa === formData.IDEmpresa && !h.DataFim
        );
        
        if (responsavelAtivo) {
          setDialogError('Já existe um responsável ativo para esta empresa. Por favor, defina uma data de fim para o responsável atual.');
          return;
        }
      }

      // Verificar sobreposição de datas - não permitir responsáveis no mesmo dia
      const sobreposicao = historico.find(h => 
        h.IDEmpresa === formData.IDEmpresa &&
        h.ID !== editingHistorico?.ID &&
        (
          // Verificar se a data de início do novo registro conflita com período existente
          (formData.DataInicio >= new Date(h.DataInicio) && 
           (!h.DataFim || formData.DataInicio <= new Date(h.DataFim))) ||
          
          // Verificar se a data de fim do novo registro conflita com período existente
          (formData.DataFim && 
           new Date(h.DataInicio) >= formData.DataInicio && 
           (!h.DataFim || new Date(h.DataInicio) <= formData.DataFim)) ||
           
          // Verificar se o período existente está completamente dentro do novo período
          (formData.DataFim && 
           new Date(h.DataInicio) >= formData.DataInicio && 
           (!h.DataFim || new Date(h.DataFim) <= formData.DataFim))
        )
      );

      if (sobreposicao) {
        setDialogError('Não é permitido que dois responsáveis sejam registrados para a mesma empresa no mesmo período. O novo responsável deve iniciar pelo menos um dia após o término do responsável anterior.');
        return;
      }

      // Preparar dados para envio, convertendo datas para formato string
      const dadosParaEnvio = {
        IDEmpresa: formData.IDEmpresa,
        IDResponsavel: formData.IDResponsavel,
        DataInicio: formData.DataInicio ? formData.DataInicio.toISOString().split('T')[0] : null,
        DataFim: formData.DataFim ? formData.DataFim.toISOString().split('T')[0] : null,
      };

      console.log('Dados sendo enviados:', dadosParaEnvio); // Debug

      if (editingHistorico) {
        await api.put(`/historico-responsaveis/${editingHistorico.ID}`, dadosParaEnvio);
        setSuccess('Histórico atualizado com sucesso!');
      } else {
        await api.post('/historico-responsaveis', dadosParaEnvio);
        setSuccess('Histórico adicionado com sucesso!');
      }
      
      setOpenDialog(false);
      setEditingHistorico(null);
      setDialogError(''); // Limpar erro do dialog
      setFormData({
        IDEmpresa: '',
        IDResponsavel: '',
        DataInicio: new Date(),
        DataFim: null,
        empresaSelecionada: null,
        responsavelSelecionado: null,
      });
      fetchHistorico(paginacao.pagina_atual); // Recarregar página atual
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao salvar histórico:', error);
      
      // Verificar se é um erro do backend com mensagem específica
      if (error.response && error.response.data && error.response.data.message) {
        setDialogError(error.response.data.message);
      } else if (error.response && error.response.status === 400) {
        setDialogError('Erro de validação. Verifique os dados e tente novamente.');
      } else {
        setDialogError('Erro ao salvar. Por favor, verifique os dados e tente novamente.');
      }
    }
  };

  const handleEdit = (historico) => {
    // Encontrar os objetos completos para o Autocomplete
    const empresaObj = Array.isArray(empresas) ? empresas.find(e => e.ID === historico.IDEmpresa) : null;
    const responsavelObj = Array.isArray(responsaveis) ? responsaveis.find(r => r.id === historico.IDResponsavel) : null;
    
    setEditingHistorico(historico);
    setFormData({
      IDEmpresa: historico.IDEmpresa,
      IDResponsavel: historico.IDResponsavel,
      DataInicio: new Date(historico.DataInicio),
      DataFim: historico.DataFim ? new Date(historico.DataFim) : null,
      empresaSelecionada: empresaObj || null,
      responsavelSelecionado: responsavelObj || null,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (historico) => {
    setEditingHistorico(historico);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/historico-responsaveis/${editingHistorico.ID}`);
      setSuccess('Registro excluído com sucesso!');
      fetchHistorico(paginacao.pagina_atual); // Recarregar página atual
      setOpenConfirmDialog(false);
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao excluir registro:', error);
      setError('Erro ao excluir. Por favor, tente novamente.');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Responsável / Empresa
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerenciar histórico de responsáveis por empresa (corretora)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={showFilters ? "contained" : "outlined"}
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterIcon />}
            >
              Filtros
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setEditingHistorico(null);
                setFormData({
                  IDEmpresa: '',
                  IDResponsavel: '',
                  DataInicio: new Date(),
                  DataFim: null,
                  empresaSelecionada: null,
                  responsavelSelecionado: null,
                });
                setOpenDialog(true);
              }}
              startIcon={<AddIcon />}
            >
              Novo Registro
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Painel de Filtros */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filtros de Pesquisa
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Empresa"
                    value={filtros.empresa}
                    onChange={(e) => handleFilterChange('empresa', e.target.value)}
                    placeholder="Nome da empresa"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Responsável"
                    value={filtros.responsavel}
                    onChange={(e) => handleFilterChange('responsavel', e.target.value)}
                    placeholder="Nome do responsável"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Início - De"
                      value={filtros.data_inicio_de}
                      onChange={(newValue) => handleFilterChange('data_inicio_de', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Início - Até"
                      value={filtros.data_inicio_ate}
                      onChange={(newValue) => handleFilterChange('data_inicio_ate', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Fim - De"
                      value={filtros.data_fim_de}
                      onChange={(newValue) => handleFilterChange('data_fim_de', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Fim - Até"
                      value={filtros.data_fim_ate}
                      onChange={(newValue) => handleFilterChange('data_fim_ate', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={aplicarFiltros}
                    sx={{ height: '56px' }}
                  >
                    Aplicar Filtros
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={limparFiltros}
                    startIcon={<ClearIcon />}
                    sx={{ height: '56px' }}
                  >
                    Limpar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Informações de paginação */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {historico.length} de {paginacao.total_registros} registros
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Página {paginacao.pagina_atual} de {paginacao.total_paginas}
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={ordenacao.order_by === 'ID'}
                    direction={ordenacao.order_by === 'ID' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('ID')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'NomeFantasia'}
                    direction={ordenacao.order_by === 'NomeFantasia' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('NomeFantasia')}
                  >
                    Nome Fantasia
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'NomeResponsavel'}
                    direction={ordenacao.order_by === 'NomeResponsavel' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('NomeResponsavel')}
                  >
                    Responsável
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'DataInicio'}
                    direction={ordenacao.order_by === 'DataInicio' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('DataInicio')}
                  >
                    Data Início
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'DataFim'}
                    direction={ordenacao.order_by === 'DataFim' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('DataFim')}
                  >
                    Data Fim
                  </TableSortLabel>
                </TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Carregando dados...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : historico.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum registro encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                historico.map((registro) => (
                  <TableRow key={registro.ID}>
                    <TableCell>{registro.ID}</TableCell>
                    <TableCell>{registro.NomeFantasia}</TableCell>
                    <TableCell>{registro.NomeResponsavel}</TableCell>
                    <TableCell>{formatDate(registro.DataInicio)}</TableCell>
                    <TableCell>{formatDate(registro.DataFim)}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(registro)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(registro)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Componente de Paginação */}
        {paginacao.total_paginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={paginacao.total_paginas}
              page={paginacao.pagina_atual}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}

        {/* Dialog para adicionar/editar registro */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingHistorico ? 'Editar Registro' : 'Novo Registro'}
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
                {dialogError}
              </Alert>
            )}
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                fullWidth
                options={empresas}
                getOptionLabel={(option) => {
                  const idStr = option?.ID != null ? String(option.ID) : '';
                  const nome = option?.NomeFantasia ?? '';
                  return idStr ? `${idStr} - ${nome}` : nome;
                }}
                value={formData.empresaSelecionada}
                onChange={(event, newValue) => {
                  setFormData({ 
                    ...formData, 
                    IDEmpresa: newValue ? newValue.ID : '',
                    empresaSelecionada: newValue 
                  });
                }}
                isOptionEqualToValue={(option, value) => option.ID === value.ID}
                filterOptions={(options, { inputValue }) => {
                  const term = inputValue?.toLowerCase() ?? '';
                  return options.filter(option => {
                    const nome = option?.NomeFantasia?.toLowerCase() ?? '';
                    const idStr = option?.ID != null ? String(option.ID) : '';
                    return nome.includes(term) || idStr.includes(inputValue);
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empresa"
                    placeholder="Digite para buscar empresa..."
                    required
                  />
                )}
                noOptionsText="Nenhuma empresa encontrada"
              />

              <Autocomplete
                fullWidth
                options={responsaveis}
                getOptionLabel={(option) => option.Responsavel || ''}
                value={formData.responsavelSelecionado}
                onChange={(event, newValue) => {
                  setFormData({ 
                    ...formData, 
                    IDResponsavel: newValue ? newValue.id : '',
                    responsavelSelecionado: newValue 
                  });
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.Responsavel.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsável"
                    placeholder="Digite para buscar responsável..."
                    required
                  />
                )}
                noOptionsText="Nenhum responsável encontrado"
              />

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  label="Data Início"
                  value={formData.DataInicio}
                  onChange={(newValue) => {
                    setFormData({ ...formData, DataInicio: newValue });
                  }}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  label="Data Fim (opcional)"
                  value={formData.DataFim}
                  onChange={(newValue) => {
                    setFormData({ ...formData, DataFim: newValue });
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDialog(false);
              setDialogError(''); // Limpar erro ao fechar
            }}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmação para exclusão */}
        <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmDialog(false)}>Cancelar</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default HistoricoResponsaveisPanel; 